import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    // background glyphs
    MonitorHeart, DeviceThermostat, Favorite, Bloodtype, Vaccines,
    Medication, Science, MedicalServices, Air, Healing, WaterDrop, Biotech,
    // module glyphs
    Assignment, Hotel, Emergency, LocalPharmacy, ReceiptLong, Payments,
    Inventory2, Groups, Assessment, AdminPanelSettings,
    EventAvailable, Scanner, HealthAndSafety, AccountBalance, Sms,
    MenuBook, AccountTree, Summarize, FactCheck, LocalShipping,
    Forum, PhoneInTalk, AutoAwesome,
    // compliance
    Security, VerifiedUser, Lock,
    // library button
    ArrowForwardIos as ArrowIcon,
} from '@mui/icons-material';

import { BRAND, PRODUCT, FONT_STACK } from '../../brand';

// ============================================================================
//  BrandAside — the information panel on the sign-in screen
// ============================================================================
//
//  Visual language is clinical instrumentation rather than corporate gradient:
//  a live cardiac rhythm, a field of vital-sign glyphs, and a monitor-style
//  frame around the product blurb.
//
//  Below that, the twelve modules the system actually ships — taken from the
//  real route table in App.js, not an aspirational list. They cycle a highlight
//  one at a time so the breadth registers without the panel shouting.

const ACCENT    = BRAND.green;
const ACCENT_LT = BRAND.greenLight;

// ── Cardiac rhythm ──────────────────────────────────────────────────────────
// One beat: baseline → P wave → QRS complex → T wave. Exactly 150 units wide,
// which is what lets the trace scroll seamlessly: translating the whole group
// by one beat-width lands the pattern back on itself with no visible seam.
const BEAT_W = 150;

// How long one beat takes to sweep past, in seconds.
//
// Was 1.15s. At that speed the waveform whipped across the card and read as an
// animation rather than an instrument — the eye never settles on a complex
// long enough to recognise it as a heartbeat. Halved.
//
// It is a single constant because three things have to agree on it: the
// foreground trace, the slower parallax trace behind it, and the blip at the
// right-hand edge. When those were three separate literals, changing the speed
// meant the blip stopped landing on the QRS and the trace looked like it was
// ticking to something that was not there.
const BEAT_S    = 2.3;
const BEAT_BG_S = BEAT_S * 3;   // background layer, deliberately out of step

const beat = (x, y) =>
    `M${x} ${y} h26 q7 -7 14 0 h16 l5 9 l7 -26 l7 21 l5 -4 h14 q12 -12 24 0 h32`;

const rhythm = (beats, y) =>
    Array.from({ length: beats }, (_, i) => beat(i * BEAT_W, y)).join(' ');

// ── Background glyph field ──────────────────────────────────────────────────
// Fixed positions, not randomised: a random layout would reshuffle on every
// re-render and the panel would visibly twitch.
const GLYPHS = [
    { Icon: MonitorHeart,     top: '4%',  left: '60%', size: 104, rot: -12, op: .05  },
    { Icon: DeviceThermostat, top: '14%', left: '88%', size: 76,  rot: 14,  op: .045 },
    { Icon: Favorite,         top: '34%', left: '80%', size: 88,  rot: -8,  op: .04  },
    { Icon: Air,              top: '58%', left: '90%', size: 78,  rot: 10,  op: .042 },
    { Icon: Bloodtype,        top: '70%', left: '66%', size: 70,  rot: -14, op: .038 },
    { Icon: Science,          top: '88%', left: '82%', size: 74,  rot: 8,   op: .04  },
    { Icon: Vaccines,         top: '26%', left: '3%',  size: 66,  rot: 16,  op: .035 },
    { Icon: Medication,       top: '80%', left: '8%',  size: 72,  rot: -10, op: .035 },
    { Icon: MedicalServices,  top: '52%', left: '2%',  size: 62,  rot: 12,  op: .032 },
    { Icon: Healing,          top: '93%', left: '36%', size: 58,  rot: -6,  op: .03  },
    { Icon: WaterDrop,        top: '8%',  left: '34%', size: 52,  rot: 18,  op: .03  },
    { Icon: Biotech,          top: '44%', left: '48%', size: 56,  rot: -16, op: .026 },
];

// ── The modules ─────────────────────────────────────────────────────────────
// Drawn from what the system actually ships — the route table in App.js plus
// the sub-modules under each panel (claims covers private insurers as well as
// NHIA; wards covers the nursing station and medication administration; the
// admin panel carries SOPs, messaging, audit and multi-branch).
//
// Grouped so the grid reads in the order a patient moves through the
// facility: front desk → clinical → diagnostics → revenue → supply →
// governance.
const MODULES = [
    // front desk
    { Icon: Assignment,          label: 'Patient Records' },
    { Icon: EventAvailable,      label: 'Appointments'    },
    { Icon: Emergency,           label: 'Emergency'       },
    // clinical
    { Icon: MedicalServices,     label: 'Doctors Panel'   },
    { Icon: Hotel,               label: 'Wards & Beds'    },
    { Icon: Healing,             label: 'Nursing Station' },
    { Icon: Medication,          label: 'Medication Chart'},
    // diagnostics
    { Icon: Biotech,             label: 'Laboratory'      },
    { Icon: Scanner,             label: 'Radiology'       },
    { Icon: LocalPharmacy,       label: 'Pharmacy'        },
    // revenue
    { Icon: ReceiptLong,         label: 'NHIS Claims'     },
    { Icon: HealthAndSafety,     label: 'Private Insurance' },
    { Icon: Payments,            label: 'Billing'         },
    { Icon: AccountBalance,      label: 'Accounts'        },
    // supply chain
    { Icon: Inventory2,          label: 'Store & Stock'   },
    { Icon: LocalShipping,       label: 'Procurement'     },
    { Icon: Vaccines,            label: 'Consumables'     },
    // information & governance
    { Icon: Summarize,           label: 'Health Information' },
    { Icon: Assessment,          label: 'Reports'         },
    { Icon: MenuBook,            label: 'Policies & SOPs' },
    // communication — the in-app intercom (Components/Call/*, context/Chatcontext)
    { Icon: Forum,               label: 'Staff Chat'      },
    { Icon: PhoneInTalk,         label: 'Intercom & Calls' },
    { Icon: Sms,                 label: 'SMS Alerts'      },
    // intelligence — AI clinical note assistance (Common/AI*Dialog)
    { Icon: AutoAwesome,         label: 'AI Assistant'    },
    // administration
    { Icon: Groups,              label: 'HR & Payroll'    },
    { Icon: AccountTree,         label: 'Branches'        },
    { Icon: FactCheck,           label: 'Audit Trail'     },
    { Icon: AdminPanelSettings,  label: 'Administration'  },
];

function BrandAside({ onOpenLibrary }) {
    // Cycles the highlight through the module grid. Purely decorative, so it
    // stops entirely for anyone who has asked for reduced motion rather than
    // running an invisible timer.
    const [live, setLive] = useState(0);

    useEffect(() => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduced) return undefined;
        const t = setInterval(() => setLive(i => (i + 1) % MODULES.length), 1900);
        return () => clearInterval(t);
    }, []);

    return (
        <Box
            sx={{
                position: 'relative', overflow: 'hidden',
                background: `linear-gradient(150deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
                color: '#fff', fontFamily: FONT_STACK,
                display: 'flex', flexDirection: 'column',
                px: { xs: 3, sm: 5, lg: 5.5, xl: 7 },
                py: { xs: 4, lg: 5 },
                minHeight: { xs: 'auto', lg: '100%' },
            }}
        >
            <style>{`
                @keyframes riseIn    { from { opacity:0; transform: translateY(14px) } to { opacity:1; transform: none } }
                @keyframes drift     { 0%,100% { transform: translate(0,0) scale(1) } 50% { transform: translate(-3%,3%) scale(1.06) } }
                @keyframes ecgScroll { from { transform: translateX(0) } to { transform: translateX(-${BEAT_W}px) } }
                @keyframes blip      { 0%,100% { opacity:.35; transform: scale(1) } 50% { opacity:1; transform: scale(1.35) } }
                @keyframes softPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(63,191,100,.55) } 50% { box-shadow: 0 0 0 7px rgba(63,191,100,0) } }

                .ba-rise   { animation: riseIn .55s ease-out both; }
                .ba-glow   { animation: drift 22s ease-in-out infinite; }
                /* One beat-width per cycle, linear, forever — the strip-chart
                   sweep of a bedside monitor. All three share BEAT_S so the
                   blip keeps landing on the QRS as it passes. */
                .ba-ecg    { animation: ecgScroll ${BEAT_S}s linear infinite; }
                .ba-ecg-bg { animation: ecgScroll ${BEAT_BG_S}s linear infinite; }
                .ba-blip   { animation: blip ${BEAT_S}s ease-in-out infinite; }
                .ba-dot    { animation: softPulse 2.2s ease-in-out infinite; }

                @media (prefers-reduced-motion: reduce) {
                    .ba-rise, .ba-glow, .ba-ecg, .ba-ecg-bg, .ba-blip, .ba-dot {
                        animation: none !important;
                    }
                }
            `}</style>

            {/* ══ backdrop ══ */}
            <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <Box className="ba-glow" sx={{ position: 'absolute', top: '-22%', right: '-18%', width: 620, height: 620, borderRadius: '50%', background: `radial-gradient(circle, ${ACCENT}30 0%, transparent 62%)` }} />
                <Box sx={{ position: 'absolute', bottom: '-28%', left: '-22%', width: 560, height: 560, borderRadius: '50%', background: `radial-gradient(circle, ${BRAND.navyLight}55 0%, transparent 66%)` }} />

                {/* monitor graticule — fine over coarse */}
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.022) 1px,transparent 1px)', backgroundSize: '26px 26px' }} />
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)', backgroundSize: '130px 130px' }} />

                {GLYPHS.map(({ Icon, top, left, size, rot, op }, i) => (
                    <Icon
                        key={i}
                        sx={{
                            position: 'absolute', top, left,
                            fontSize: { xs: size * .6, lg: size },
                            color: '#fff', opacity: op, transform: `rotate(${rot}deg)`,
                            display: { xs: i > 5 ? 'none' : 'block', lg: 'block' },
                        }}
                    />
                ))}

                {/* slow trace drifting behind everything */}
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: '40%', opacity: .06 }}>
                    <svg width="100%" height="64" viewBox="0 0 900 64" preserveAspectRatio="none">
                        <g className="ba-ecg-bg">
                            <path d={rhythm(9, 34)} fill="none" stroke={ACCENT_LT} strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                    </svg>
                </Box>
            </Box>

            {/* ══ content ══ */}
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

                {/* brand lockup */}
                <Box className="ba-rise" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, lg: 2 }, animationDelay: '.05s' }}>
                    <Box
                        sx={{
                            width: { xs: 46, lg: 58 }, height: { xs: 46, lg: 58 }, borderRadius: '15px', flexShrink: 0,
                            background: `linear-gradient(135deg, ${ACCENT_LT} 0%, ${ACCENT} 100%)`,
                            display: 'grid', placeItems: 'center', boxShadow: `0 10px 26px ${ACCENT}52`,
                        }}
                    >
                        <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, lg: 23 }, color: '#fff', letterSpacing: '-.6px' }}>GA</Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: { xs: 19, lg: 25 }, fontWeight: 900, letterSpacing: '.5px', lineHeight: 1.05 }}>
                            {PRODUCT.vendor}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 10.5, lg: 12 }, fontWeight: 600, color: 'rgba(255,255,255,.6)', letterSpacing: '1.1px', textTransform: 'uppercase', mt: .375 }}>
                            {PRODUCT.vendorTagline}
                        </Typography>
                    </Box>
                </Box>

                {/* hero */}
                <Box sx={{ flex: { lg: 1 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: { xs: 3, lg: 3.5 } }}>

                    <Typography
                        className="ba-rise" component="h1"
                        sx={{
                            animationDelay: '.11s',
                            fontSize: { xs: '2.7rem', sm: '3.4rem', lg: 'clamp(3rem, 4vw, 4.1rem)' },
                            fontWeight: 900, lineHeight: .92, letterSpacing: '-2.1px', color: '#fff',
                        }}
                    >
                        {PRODUCT.name}
                    </Typography>

                    <Box className="ba-rise" sx={{ animationDelay: '.11s', width: 54, height: 4, borderRadius: 2, background: ACCENT_LT, mt: 1.75, mb: 1.75 }} />

                    <Typography
                        className="ba-rise"
                        sx={{
                            animationDelay: '.17s',
                            fontSize: { xs: '.76rem', lg: '.88rem' }, fontWeight: 800, color: ACCENT_LT,
                            textTransform: 'uppercase', letterSpacing: '.6px', lineHeight: 1.45,
                        }}
                    >
                        {PRODUCT.fullName}
                    </Typography>

                    {/* ══ monitor readout — live trace over the blurb ══
                        Full panel width. It used to be capped at 520px, which
                        left most of the right-hand half empty. */}
                    <Box
                        className="ba-rise"
                        sx={{
                            animationDelay: '.23s',
                            mt: 2.75, width: '100%', borderRadius: '14px', overflow: 'hidden',
                            background: 'rgba(255,255,255,.06)',
                            border: '1px solid rgba(255,255,255,.15)',
                            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                        }}
                    >
                        <Box sx={{ position: 'relative', height: 46, background: 'rgba(0,0,0,.18)', borderBottom: '1px solid rgba(255,255,255,.12)', overflow: 'hidden' }}>
                            {/* Nine beats across a 900-unit window: the extra
                                beats are what the scroll reveals, so the trace
                                never runs out of chart to sweep through. */}
                            <svg width="100%" height="46" viewBox="0 0 900 46" preserveAspectRatio="none"
                                style={{ position: 'absolute', inset: 0 }}>
                                <g className="ba-ecg">
                                    <path d={rhythm(9, 26)} fill="none" stroke={ACCENT_LT}
                                        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                            </svg>

                            <Box sx={{ position: 'absolute', top: 7, left: 12, display: 'flex', alignItems: 'center', gap: .75 }}>
                                <Box className="ba-dot" sx={{ width: 6.5, height: 6.5, borderRadius: '50%', background: ACCENT_LT }} />
                                <Typography sx={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,.6)', letterSpacing: '1.2px' }}>
                                    SYSTEM ACTIVE
                                </Typography>
                            </Box>

                            {/* pulse blip on the right edge, in time with the sweep */}
                            <Box className="ba-blip" sx={{ position: 'absolute', top: '50%', right: 12, mt: '-3px', width: 6, height: 6, borderRadius: '50%', background: ACCENT_LT }} />
                        </Box>

                        <Box sx={{ px: { xs: 2, lg: 2.75 }, py: { xs: 1.75, lg: 2 } }}>
                            <Typography sx={{ fontSize: { xs: 12.5, lg: 14 }, color: 'rgba(255,255,255,.8)', lineHeight: 1.8 }}>
                                {PRODUCT.blurb}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ══ modules ══
                        Also full width. auto-fill rather than a fixed column
                        count, so the grid grows a column whenever the panel is
                        wide enough for one instead of leaving a dead margin. */}
                    <Box className="ba-rise" sx={{ animationDelay: '.29s', mt: 3, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,.55)', letterSpacing: '1.4px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                One system · every department &amp; function
                            </Typography>
                            <Box sx={{ flex: 1, height: 1, background: 'rgba(255,255,255,.14)' }} />
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: ACCENT_LT, whiteSpace: 'nowrap' }}>
                                {MODULES.length} modules
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0,1fr))',
                                    sm: 'repeat(auto-fill, minmax(158px, 1fr))',
                                },
                                gap: { xs: .75, lg: .875 },
                            }}
                        >
                            {MODULES.map(({ Icon, label }, i) => {
                                const on = i === live;
                                return (
                                    <Box
                                        key={label}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: .75,
                                            px: 1.125, py: .85, borderRadius: '9px', minWidth: 0,
                                            background: on ? `${ACCENT}26` : 'rgba(255,255,255,.05)',
                                            border: `1px solid ${on ? `${ACCENT_LT}80` : 'rgba(255,255,255,.11)'}`,
                                            boxShadow: on ? `0 4px 16px ${ACCENT}33` : 'none',
                                            transform: on ? 'translateY(-1px)' : 'none',
                                            transition: 'background .5s ease, border-color .5s ease, box-shadow .5s ease, transform .5s ease',
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 15, flexShrink: 0,
                                                color: on ? ACCENT_LT : 'rgba(255,255,255,.5)',
                                                transition: 'color .5s ease',
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 10, lg: 10.5 }, fontWeight: 700, lineHeight: 1.25,
                                                color: on ? '#fff' : 'rgba(255,255,255,.66)',
                                                transition: 'color .5s ease',
                                            }}
                                            noWrap
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ══ Policy & SOP library ══
                        The library used to occupy a whole column of this panel,
                        which was a lot of real estate for something nobody reads
                        while trying to sign in. It is one button now — the
                        content is unchanged, it just waits to be asked for. */}
                    <Box
                        className="ba-rise"
                        component="button"
                        type="button"
                        onClick={onOpenLibrary}
                        sx={{
                            animationDelay: '.35s',
                            mt: 2.5, width: '100%', textAlign: 'left', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 1.75,
                            px: { xs: 1.75, lg: 2.25 }, py: { xs: 1.5, lg: 1.75 },
                            borderRadius: '13px', font: 'inherit',
                            background: 'rgba(255,255,255,.07)',
                            border: '1px solid rgba(255,255,255,.18)',
                            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                            transition: 'background .18s ease, border-color .18s ease, transform .18s ease',
                            '&:hover': {
                                background: `${ACCENT}1F`,
                                borderColor: `${ACCENT_LT}80`,
                                transform: 'translateY(-2px)',
                            },
                            '&:hover .ba-lib-arrow': { transform: 'translateX(3px)' },
                            '&:focus-visible': { outline: `2px solid ${ACCENT_LT}`, outlineOffset: 2 },
                        }}
                    >
                        <Box
                            sx={{
                                width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                                background: `linear-gradient(135deg, ${ACCENT_LT} 0%, ${ACCENT} 100%)`,
                                display: 'grid', placeItems: 'center',
                                boxShadow: `0 6px 16px ${ACCENT}45`,
                            }}
                        >
                            <MenuBook sx={{ fontSize: 19, color: '#fff' }} />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: { xs: 13, lg: 13.5 }, fontWeight: 800, color: '#fff', lineHeight: 1.35 }}>
                                Policy &amp; SOP Library
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.55)', lineHeight: 1.4 }}>
                                Browse every published procedure, step by step
                            </Typography>
                        </Box>

                        <ArrowIcon className="ba-lib-arrow" sx={{ fontSize: 13, color: ACCENT_LT, flexShrink: 0, transition: 'transform .18s ease' }} />
                    </Box>
                </Box>

                {/* compliance footing */}
                <Box className="ba-rise" sx={{ animationDelay: '.35s' }}>
                    <Box sx={{ display: 'flex', gap: { xs: 2, lg: 3 }, flexWrap: 'wrap', pt: { xs: 2, lg: 2.5 }, borderTop: '1px solid rgba(255,255,255,.12)' }}>
                        {[[Security, 'HIPAA Compliant'], [VerifiedUser, 'ISO 27001'], [Lock, '256-bit TLS']].map(([Icon, label]) => (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                                <Icon sx={{ fontSize: 15, color: ACCENT_LT }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.7)' }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={{ display: { xs: 'none', lg: 'block' }, fontSize: 11, color: 'rgba(255,255,255,.32)', mt: 2 }}>
                        © {new Date().getFullYear()} {PRODUCT.vendor}. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default BrandAside;
